import { IListingForBoundsParams } from './types'
import {
  MAX_FETCHED_LISTINGS,
  PAGE_COUNT,
} from '../../constants/paginationConstants'

interface IPolygonShape {
  rings: number[][][]
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

const polygonShapes = new WeakMap<object, IPolygonShape>()

const toPolygonShape = (polygon: any): IPolygonShape => {
  const cached = polygonShapes.get(polygon)

  if (cached) {
    return cached
  }

  const rings: number[][][] = polygon
    .getPaths()
    .getArray()
    .map((path: any) =>
      path.getArray().map((point: any) => [point.lng(), point.lat()])
    )
  const shape = {
    rings,
    maxLat: -Infinity,
    maxLng: -Infinity,
    minLat: Infinity,
    minLng: Infinity,
  }

  rings.forEach((ring) =>
    ring.forEach(([lng, lat]) => {
      shape.minLat = Math.min(shape.minLat, lat)
      shape.maxLat = Math.max(shape.maxLat, lat)
      shape.minLng = Math.min(shape.minLng, lng)
      shape.maxLng = Math.max(shape.maxLng, lng)
    })
  )
  polygonShapes.set(polygon, shape)
  return shape
}

const ringContains = (ring: number[][], lng: number, lat: number) => {
  let inside = false

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [lngI, latI] = ring[i]
    const [lngJ, latJ] = ring[j]

    if (
      latI > lat !== latJ > lat &&
      lng < ((lngJ - lngI) * (lat - latI)) / (latJ - latI) + lngI
    ) {
      inside = !inside
    }
  }
  return inside
}

export const polygonContains = (
  polygon: any,
  latitude: number,
  longitude: number
) => {
  const { rings, minLat, maxLat, minLng, maxLng } = toPolygonShape(polygon)

  if (
    latitude < minLat ||
    latitude > maxLat ||
    longitude < minLng ||
    longitude > maxLng
  ) {
    return false
  }

  return rings.reduce(
    (inside, ring) =>
      ringContains(ring, longitude, latitude) ? !inside : inside,
    false
  )
}

export const filterListingsForBounds = async ({
  listings,
  bounds,
  filtered,
  triangle,
}: IListingForBoundsParams) => {
  if (bounds !== null && !isNaN(bounds.NE.lat)) {
    const { NE, SW } = bounds
    const ix = NE.lat
    const iy = NE.lng
    const ax = SW.lat
    const ay = SW.lng

    const filteredListings = listings.filter((listing) => {
      const { latitude, longitude } = listing.address
      if (
        ix >= latitude &&
        latitude >= ax &&
        iy >= longitude &&
        longitude >= ay
      ) {
        if (filtered && triangle) {
          return triangle.some((polygon: any) =>
            polygonContains(polygon, Number(latitude), Number(longitude))
          )
        }
        return true
      }
      return false
    })
    return filteredListings
  }
  return listings
}

export const getSliceValues = (pageNumber: number) => {
  return {
    sliceEnd: PAGE_COUNT * pageNumber,
    sliceStart: PAGE_COUNT * (pageNumber - 1),
  }
}

export const getRemainingPageParams = (params: any, totalCount: number) => {
  const { page, pageCount } = params
  const fetchableCount = Math.min(totalCount, MAX_FETCHED_LISTINGS)
  const totalPages = Math.ceil(fetchableCount / pageCount)
  const remainingPages = []

  for (let nextPage = page + 1; nextPage <= totalPages; nextPage++) {
    remainingPages.push({ ...params, page: nextPage })
  }

  return remainingPages
}
